import { api } from '@/api';
import { RadialTeamSelect } from '@/components/onboarding/RadialTeamSelect';
import { useDeviceId, useRoleSelection, useStore, useTeamSelection, type PlayerState } from '@/engine/store';
import type { TeamName, TeamRole } from '@shared/modelTypes/session-types';
import { createFileRoute } from '@tanstack/react-router';
import { TrainFrontTunnel } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { setUser as sentrySetUser } from '@sentry/browser';

export const Route = createFileRoute('/set-player')({
	component: RouteComponent,
});

function RouteComponent() {
	const sessionId = useStore((s) => s.location?.sessionId);
	const deviceId = useDeviceId();
	// const sessionId = useSessionId();
	const setPlayerState = useStore((s) => s.setPlayerState);

	async function setPlayer(data: PlayerState) {
		sentrySetUser({
			id: deviceId,
			username: data.id ?? 'unknown',
		});

		// this should never happen, router will always redirect if session ID is not available
		const result = await api.setPlayer({
			team: data.team,
			role: data.teamRole,
			deviceId: deviceId,
			sessionId: sessionId!,
		});

		if (!result.success) {
			// setTeamName(null);
			// toast('Role no longer available, please select other role');
			toast(result.message);
			return false;
		} else {
			setPlayerState({ ...data });
			return true;
		}
	}

	if (!sessionId) return null;
	return <PlayerSelection onSelect={setPlayer} />;
}

function PlayerSelection({ onSelect }: { onSelect: (data: PlayerState) => Promise<boolean> }) {
	const [teamName, setTeamName] = useState<TeamName | null>(null);

	const force = false;
	return (
		<>
			<TeamSelection force={force} onSubmit={setTeamName} />
			{teamName && (
				<RoleSelection
					force={force}
					teamName={teamName}
					onSubmit={async (role) => {
						const res = await onSelect({ id: `${teamName}-${role}`, team: teamName, teamRole: role });
						// select failed, reset state
						if (!res) setTeamName(null);
					}}
					onBack={() => {
						setTeamName(null);
					}}
				/>
			)}
		</>
	);
}

function TeamSelection({ force, onSubmit }: { force?: boolean; onSubmit: (teamName: TeamName) => void }) {
	const teamStates = useTeamSelection();
	const [started, setStarted] = useState(false);

	useEffect(() => {
		const id = requestAnimationFrame(() => setStarted(true));
		return () => cancelAnimationFrame(id);
	}, []);

	return (
		<div
			className={`absolute grid size-full transform grid-cols-2 grid-rows-2 p-1 font-extrabold transition-transform duration-300 ease-out ${started ? 'translate-y-0' : 'translate-y-[100vh]'}`}
		>
			<div
				className={`absolute top-1/2 left-1/2 z-50 flex aspect-square size-[50vh] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black transition-transform duration-300 ease-out`}
			>
				<p className={`responsive-text-xl font-semibold transition-opacity duration-300 ease-out`}>Kies een team!</p>
			</div>
			{teamStates.map((team, i) => {
				return (
					<TeamButton
						key={`${team.name}-${i}-team-button`}
						name={team.name}
						disabled={!team.joinable && !force}
						playerCount={team.existingPlayerCount}
						maxPlayers={team.maxPlayerCount}
						i={i}
						onClick={() => onSubmit(team.name)}
					/>
				);
			})}
		</div>
	);
}

function RoleSelection({
	force,
	teamName,
	onSubmit,
	onBack,
}: {
	force?: boolean;
	teamName: TeamName;
	onSubmit: (role: TeamRole) => void;
	onBack: () => void;
}) {
	const [finished, setFinished] = useState(false);
	const roleStates = useRoleSelection(teamName);
	return (
		<RadialTeamSelect
			open={true}
			finished={finished}
			options={roleStates}
			force={force}
			onFinish={(role) => {
				setFinished(true);
				onSubmit(role);
			}}
			onClose={onBack}
		/>
	);
}

function TeamButton({
	onClick,
	name,
	disabled,
	playerCount,
	maxPlayers,
	i,
}: {
	onClick: () => void;
	name: string;
	disabled: boolean;
	playerCount: number;
	maxPlayers: number;
	i: number;
}) {
	const buttonStyle = `responsive-text-4xl overflow-hidden relative  m-1 flex flex-col items-center justify-center rounded-lg border  uppercase transition-all duration-150 `;
	let colors = '';
	let corner = '';
	let row = '';
	let col = '';
	let rotate = '';

	switch (i + 1) {
		case 1:
			colors =
				'border-purple-400 bg-purple-400/40 bg-radial-[at_90%_75%] from-sky-100/45 to-purple-700/40 text-purple-300 hover:bg-purple-300/40 hover:text-purple-200 active:bg-purple-300/45';
			corner = 'rounded-br-[25.25vh] [corner-bottom-right-shape:scoop]';
			row = 'row-start-1';
			col = 'col-start-1';
			rotate = '-left-32';
			break;
		case 2:
			colors =
				'border-yellow-400 bg-yellow-400/40 bg-radial-[at_10%_75%] from-sky-100/45 to-yellow-700/40 text-yellow-400 hover:bg-yellow-300/40 hover:text-yellow-300 active:bg-yellow-300/45';
			corner = 'rounded-bl-[25.25vh] [corner-bottom-left-shape:scoop]';
			row = 'row-start-1';
			col = 'col-start-2';
			rotate = '-right-32';
			break;
		case 3:
			colors =
				'border-green-400 bg-green-400/40 bg-radial-[at_90%_25%] from-sky-100/45 to-green-700/40 text-green-400 hover:bg-green-300/40 hover:text-green-300 active:bg-green-300/45';
			corner = 'rounded-tr-[25.25vh] [corner-top-right-shape:scoop]';
			row = 'row-start-2';
			col = 'col-start-1';
			rotate = '-left-32';
			break;
		case 4:
			colors =
				'border-teal-400 bg-teal-400/40 bg-radial-[at_10%_25%] from-teal-100/45 to-teal-700/40 text-teal-300 hover:bg-teal-300/40 hover:text-teal-200 active:bg-teal-300/45';
			corner = 'rounded-tl-[25.25vh] [corner-top-left-shape:scoop]';
			row = 'row-start-2';
			col = 'col-start-2';
			rotate = '-right-32';
			break;
	}

	return (
		<button
			key={i}
			className={`${buttonStyle} ${corner} ${row} ${col} ${colors} disabled:opacity-30`}
			disabled={disabled}
			onClick={onClick}
		>
			<div className="flex flex-col items-center">
				<span>{name}</span>
				<span className="responsive-text-xl">
					({playerCount}/{maxPlayers})
				</span>
				<TrainFrontTunnel className={`absolute -bottom-28 size-92 ${rotate}`} strokeWidth={1.5} />
			</div>
		</button>
		// <>
		// 	<p>Enter your role:</p>
		// 	{roleStates.map((role) => {
		// 		return (
		// 			<Button key={role.name} disabled={!role.available && !force} onClick={() => onSubmit(role.name)}>
		// 				{role.name}
		// 			</Button>
		// 		);
		// 	})}
		// </>
	);
}
